import { QRCodeSVG } from "qrcode.react";
import { SyntheticEvent, useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";

import {
  TwoFactorMethod,
  TwoFactorSetup,
  activateTwoFactor,
  startTwoFactorSetup,
} from "@/api/twoFactor";
import { AccountCard, AccountShell } from "@/components/layouts";
import { Button } from "@/components/ui";
import { CopyButton } from "@/components/ui/CopyButton";
import { FRONT_DOOR_INPUT, Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Notice } from "@/components/ui/Notice";
import { useAuthContext } from "@/context/auth";

import { BackupCodesPanel } from "./BackupCodesPanel";
import { describeError } from "./errorCopy";
import { outcomeState } from "./securityOutcome";
import { useResendBudget } from "./useResendBudget";

const STEPS = ["method", "connect", "codes"] as const;
const METHOD_STEP = "/security/two-factor/setup/method";

// Both the authenticator's TOTP and the emailed code are six digits (TotpVerifier.DIGITS,
// EmailCodeStore.CODE_LENGTH on the backend)
const CODE_LENGTH = 6;
const CODE_HINT_ID = "enrollment-code-hint";
const ERROR_ID = "wizard-error";

// Digits only, at most CODE_LENGTH: a code pasted as "123 456" still arrives whole, which a
// maxLength attribute would cut at the space. The email code is compared as typed, apart from trim
const toCodeInput = (value: string) => value.replace(/\D/g, "").slice(0, CODE_LENGTH);

// "JBSWY3DPEHPK3PXP" -> "JBSW Y3DP EHPK 3PXP": groups of four are easier to read and to copy by
// eye; the raw value is what the copy button puts on the clipboard
const groupSecret = (secret: string) =>
  secret
    .replace(/\s/g, "")
    .match(/.{1,4}/g)
    ?.join(" ") ?? "";

// A method choice is a row of two cards: content starts at the top whatever the text length (a
// button centres its content by default), and the edge is a step darker than the hairline so the
// two read as choices
const METHOD_OPTION =
  "flex flex-col items-start justify-start gap-1 rounded-xl border border-subtle-foreground p-4 text-left transition-colors hover:border-primary hover:bg-primary-subtle focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring";

const isMethod = (value: string | null): value is TwoFactorMethod =>
  value === "TOTP" || value === "EMAIL";

/**
 * Three-screen enrollment wizard. Step 1 picks the method. Step 2 is either the QR code, the secret
 * and the verification field together (a rejected code should not leave someone on a screen where
 * the QR and secret have already scrolled out of view), or - for email - the note that a code was
 * sent, the field, and a way to send another. Step 3 hands off to `BackupCodesPanel`, which owns
 * its own unsaved-codes guards.
 *
 * Two-step verification is already on at step 3 - `activateTwoFactor` turns it on - so that step
 * says so, and acknowledging the codes returns to `/security` with a notice rather than leaving
 * for a product. Each step has its own heading, which takes focus and names the browser tab.
 */
export const TwoFactorSetupWizard = () => {
  const { step } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuthContext();

  const methodParam = searchParams.get("method");
  const method: TwoFactorMethod | null = isMethod(methodParam) ? methodParam : null;

  const [setup, setSetup] = useState<TwoFactorSetup | null>(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState("");
  // Whether the last confirm attempt was refused, as opposed to a failed send or setup: only the
  // former makes the code field invalid
  const [codeRejected, setCodeRejected] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const { startCooldown, recordSend, cooldownLeft, canResend } = useResendBudget();

  const onMethodStep = step === "method";
  const onCodesStep = step === "codes";
  const onConnectStep = step === "connect";
  const isRecognizedStep = onMethodStep || onConnectStep || onCodesStep;

  // `isResend` is false for the automatic send that opens the connect step and true for a send
  // triggered from the "Send a new code" button - only a resend starts the cooldown, so the link
  // is immediately usable once the page has loaded, rather than disabled by the send that put it
  // there.
  const fetchSetup = useCallback(
    (chosen: TwoFactorMethod, isResend = false) => {
      setError("");
      setCodeRejected(false);
      if (isResend) {
        startCooldown();
      }
      startTwoFactorSetup(chosen)
        .then((result) => {
          setSetup(result);
          recordSend();
        })
        .catch((e) => setError(describeError(e, chosen)));
    },
    [startCooldown, recordSend],
  );

  // An unrecognised step (a stale bookmark, a typo) and a connect step that never chose a method
  // both have nothing to show, and must not burn a fresh secret or a mailed code on a route that
  // cannot proceed - so they redirect before the setup call below ever fires.
  useEffect(() => {
    if (!isRecognizedStep || (onConnectStep && !method)) {
      navigate(METHOD_STEP, { replace: true });
      return;
    }
    if (onConnectStep && method) {
      fetchSetup(method);
    }
  }, [isRecognizedStep, onConnectStep, method, navigate, fetchSetup]);

  // The codes exist only in memory and cannot be fetched again, so a direct hit on this step - a
  // bookmark, a refresh after acknowledging - has nothing to show.
  useEffect(() => {
    if (onCodesStep && !codes) {
      navigate("/security", { replace: true });
    }
  }, [onCodesStep, codes, navigate]);

  const choose = (chosen: TwoFactorMethod) =>
    navigate(`/security/two-factor/setup/connect?method=${chosen}`);

  const confirm = async (event: SyntheticEvent) => {
    event.preventDefault();
    setError("");
    setCodeRejected(false);
    try {
      setCodes(await activateTwoFactor(code));
      setCode("");
      navigate("/security/two-factor/setup/codes");
    } catch (e) {
      setError(describeError(e, setup?.method));
      setCodeRejected(true);
    }
  };

  // Back on the status page, which says what happened and offers the way back to the product
  const finish = () => navigate("/security", { state: outcomeState("enabled") });

  // A step about to be redirected is rendered as the method step, so nothing flashes "Step 0 of 3".
  const currentStep =
    onCodesStep && codes ? "codes" : onConnectStep && method ? "connect" : "method";
  const showsCodeField = currentStep === "connect" && setup !== null;
  const stepNumber = STEPS.indexOf(currentStep) + 1;
  const stepTitle = {
    method: "Set up two-step verification",
    connect: method === "EMAIL" ? "Confirm your email address" : "Connect your authenticator app",
    codes: "Two-step verification is on",
  }[currentStep];

  // A route change does not move focus, so a keyboard or screen-reader user would stay on the
  // control that just disappeared. The heading takes it on arrival and on every step.
  useEffect(() => {
    headingRef.current?.focus();
  }, [currentStep]);

  return (
    <AccountShell title={stepTitle} headingRef={headingRef}>
      {/* About the screen as a whole, so above the card, where the status page puts its own */}
      {onCodesStep && codes && (
        <Notice tone="success" className="mb-6">
          You'll be asked for a code each time you sign in.
        </Notice>
      )}

      <AccountCard className="flex flex-col gap-6">
        <p className="text-sm text-muted-foreground">
          Step {stepNumber} of {STEPS.length}
        </p>

        {/* Beside the code field the error belongs under it; up here it is for the screens that
            have no field, such as a setup that failed to start */}
        {error && !showsCodeField && (
          <p id={ERROR_ID} role="alert" className="text-sm font-semibold text-destructive-fg">
            {error}
          </p>
        )}

        {currentStep === "method" && (
          <>
            <p>How do you want to receive your verification codes?</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <button type="button" onClick={() => choose("TOTP")} className={METHOD_OPTION}>
                <span className="block font-semibold">Authenticator app</span>
                <span className="block text-sm text-muted-foreground">
                  An app on your phone, such as Google Authenticator or Microsoft Authenticator. The
                  stronger option: it works offline and doesn't depend on your mailbox.
                </span>
              </button>
              <button type="button" onClick={() => choose("EMAIL")} className={METHOD_OPTION}>
                <span className="block font-semibold">Email code</span>
                <span className="block text-sm text-muted-foreground">
                  A code sent to {user?.username ?? "your address"} each time you sign in. Easier to
                  set up; only as safe as your mailbox.
                </span>
              </button>
            </div>
            <Link
              to="/security"
              className="inline-flex min-h-11 items-center font-bold text-primary-fg hover:underline"
            >
              Back to account security
            </Link>
          </>
        )}

        {/* Setup failed and there is nothing else on screen, so it needs a way out that is not
            AccountShell's exit to the wider product. */}
        {currentStep === "connect" && !setup && error && method && (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Button type="button" variant="brand" size="xl" onClick={() => fetchSetup(method)}>
              Try again
            </Button>
            <Link
              to="/security"
              className="inline-flex min-h-11 items-center font-bold text-primary-fg hover:underline"
            >
              Back to account security
            </Link>
          </div>
        )}

        {onCodesStep && codes && (
          <BackupCodesPanel
            codes={codes}
            onAcknowledge={finish}
            acknowledgeLabel="I've saved these codes — continue"
          />
        )}

        {currentStep === "connect" && !setup && !error && method && (
          <p role="status" className="text-sm text-muted-foreground">
            {method === "EMAIL" ? "Sending you a code…" : "Preparing your QR code…"}
          </p>
        )}

        {currentStep === "connect" && setup && (
          <form onSubmit={confirm} className="space-y-6">
            {setup.method === "TOTP" && setup.otpauthUri && (
              <>
                {/* A touch device is often the phone that holds the authenticator, so there the link
                    that opens the app comes first, as the quicker route. The QR stays everywhere:
                    a touch laptop, a tablet or a hybrid may be set up from another phone, and the
                    key below is the fallback for both. */}
                <p className="pointer-coarse:hidden">
                  Scan this QR code with your authenticator app, or type the key in by hand.
                </p>
                <p className="hidden pointer-coarse:block">
                  Open this setup in your authenticator app on this device, scan the QR code from
                  another one, or type the key in by hand.
                </p>
                <Button
                  asChild
                  variant="outline"
                  size="xl"
                  className="hidden w-full pointer-coarse:inline-flex"
                >
                  <a href={setup.otpauthUri}>Open in your authenticator app</a>
                </Button>
                {/* A white tile with a quiet zone, so the code scans against the card */}
                <div className="w-fit rounded-xl border border-border bg-white">
                  <QRCodeSVG
                    value={setup.otpauthUri}
                    size={200}
                    marginSize={4}
                    role="img"
                    aria-label="QR code for your authenticator app"
                  />
                </div>
                <div>
                  <p id="setup-key-label" className="mb-2 text-sm font-semibold">
                    Setup key
                  </p>
                  {/* A <code> takes no name, so the group carries it and a screen reader hears
                      "Setup key", then the key in its groups of four */}
                  <div
                    role="group"
                    aria-labelledby="setup-key-label"
                    className="flex items-center gap-2"
                  >
                    <code className="min-w-0 break-words rounded-xl bg-muted px-4 py-3 font-mono text-sm tracking-wider">
                      {groupSecret(setup.secret ?? "")}
                    </code>
                    <CopyButton label="setup key" value={setup.secret ?? ""} className="size-11" />
                  </div>
                </div>
              </>
            )}
            {setup.method === "EMAIL" && (
              <>
                <p>
                  We sent a code to {setup.emailHint}. Enter it below to confirm the address works.
                </p>
                {canResend ? (
                  <button
                    type="button"
                    className="inline-flex min-h-11 min-w-11 items-center text-sm text-muted-foreground underline hover:text-foreground disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
                    disabled={cooldownLeft > 0}
                    onClick={() => fetchSetup("EMAIL", true)}
                  >
                    {cooldownLeft > 0 ? `Send a new code (${cooldownLeft}s)` : "Send a new code"}
                  </button>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No more codes can be sent right now. Wait for the last one to arrive, or come
                    back in a few minutes.
                  </p>
                )}
              </>
            )}
            <div className="space-y-2">
              <Label htmlFor="enrollment-code" className="text-sm font-semibold">
                Verification code
              </Label>
              {/* No autoFocus: the step heading holds focus, so the QR and key are read before the field */}
              <Input
                id="enrollment-code"
                value={code}
                className={FRONT_DOOR_INPUT}
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9]*"
                aria-invalid={codeRejected ? true : undefined}
                aria-describedby={error ? `${CODE_HINT_ID} ${ERROR_ID}` : CODE_HINT_ID}
                onChange={(event) => {
                  setCode(toCodeInput(event.target.value));
                  setCodeRejected(false);
                }}
              />
              <p id={CODE_HINT_ID} className="text-sm text-muted-foreground">
                {setup.method === "EMAIL"
                  ? `The ${CODE_LENGTH}-digit code from the email.`
                  : `The ${CODE_LENGTH}-digit code your authenticator app shows.`}
              </p>
              {error && (
                <p id={ERROR_ID} role="alert" className="text-sm font-semibold text-destructive-fg">
                  {error}
                </p>
              )}
            </div>
            <Button type="submit" variant="brand" size="xl" className="w-full">
              Continue
            </Button>
          </form>
        )}
      </AccountCard>
    </AccountShell>
  );
};
